import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Sparkles, Lock, Mail, User, Shield, RefreshCw } from 'lucide-react';

const StarField = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(80)].map((_, i) => (
      <div
        key={i}
        className="absolute rounded-full bg-cosmic-starWhite"
        style={{
          width: Math.random() * 3 + 1 + 'px',
          height: Math.random() * 3 + 1 + 'px',
          top: Math.random() * 100 + '%',
          left: Math.random() * 100 + '%',
          animation: `twinkle ${Math.random() * 3 + 2}s ease-in-out ${Math.random() * 2}s infinite`,
          opacity: Math.random() * 0.7 + 0.3,
        }}
      />
    ))}
  </div>
);

export default function Login() {
  const { login, register, verifyMFA, resendMFACode } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSessionToken, setMfaSessionToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.mfa_required) {
          setMfaRequired(true);
          setMfaSessionToken(result.mfa_session_token);
          setError('');
        }
      } else {
        await register(formData.email, formData.password, formData.name);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await verifyMFA(mfaCode, mfaSessionToken);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    try {
      await resendMFACode(mfaSessionToken);
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    }
  };

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setMfaSessionToken('');
    setMfaCode('');
    setError('');
  };

  return (
    <div className="min-h-screen cosmic-bg relative flex items-center justify-center p-4">
      <StarField />
      
      <div 
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(155, 89, 182, 0.5) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      <div 
        className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-15 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(233, 30, 140, 0.4) 0%, transparent 70%)',
          filter: 'blur(50px)',
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block relative">
            <img 
              src="/assets/logo.png" 
              alt="The Aura Space" 
              className="w-48 h-48 mx-auto lotus-glow animate-float"
            />
          </div>
        </div>

        <div className="glass-card p-8 relative overflow-hidden">
          <div className="absolute inset-0 sacred-geometry opacity-30"></div>
          
          <div className="relative z-10">
            {/* MFA Verification Screen */}
            {mfaRequired ? (
              <div>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-cosmic-gold/20">
                    <Shield className="w-8 h-8 text-cosmic-goldLight" />
                  </div>
                  <h2 className="text-xl font-cinzel text-cosmic-goldLight mb-2">
                    Two-Factor Authentication
                  </h2>
                  <p className="text-cosmic-starWhite/60 text-sm">
                    Enter the verification code sent to your email
                  </p>
                </div>

                <form onSubmit={handleMFASubmit} className="space-y-6">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Enter 6-digit code"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="cosmic-input w-full text-center text-2xl tracking-widest"
                      maxLength={6}
                      required
                      autoFocus
                      data-testid="mfa-code-input"
                    />
                  </div>

                  {error && (
                    <div className="text-red-400 text-sm text-center py-2 px-4 rounded-lg bg-red-500/10 border border-red-500/30">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || mfaCode.length !== 6}
                    className="cosmic-btn w-full flex items-center justify-center space-x-2 disabled:opacity-50"
                    data-testid="mfa-submit-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-cosmic-goldLight/30 border-t-cosmic-goldLight rounded-full animate-spin" />
                    ) : (
                      <>
                        <Lock className="w-5 h-5" />
                        <span>Verify & Continue</span>
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center space-y-3">
                  <button
                    onClick={handleResendCode}
                    disabled={resendCooldown > 0}
                    className="text-cosmic-goldLight/80 hover:text-cosmic-goldLight text-sm disabled:opacity-50"
                  >
                    {resendCooldown > 0 ? (
                      <span className="flex items-center justify-center gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Resend code in {resendCooldown}s
                      </span>
                    ) : (
                      "Didn't receive code? Resend"
                    )}
                  </button>
                  <div>
                    <button
                      onClick={handleBackToLogin}
                      className="text-cosmic-starWhite/50 hover:text-cosmic-goldLight text-sm"
                    >
                      ← Back to login
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Login/Register Tabs */}
                <div className="flex justify-center mb-8">
                  <div className="flex bg-cosmic-deepSpace/50 rounded-lg p-1 border border-cosmic-gold/20">
                    <button
                      onClick={() => setIsLogin(true)}
                      className={`px-6 py-2 rounded-md text-sm font-cinzel transition-all duration-300 ${
                        isLogin 
                          ? 'bg-cosmic-gold/20 text-cosmic-goldLight border border-cosmic-gold/40' 
                          : 'text-cosmic-starWhite/60 hover:text-cosmic-goldLight'
                      }`}
                      data-testid="login-tab"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => setIsLogin(false)}
                      className={`px-6 py-2 rounded-md text-sm font-cinzel transition-all duration-300 ${
                        !isLogin 
                          ? 'bg-cosmic-gold/20 text-cosmic-goldLight border border-cosmic-gold/40' 
                          : 'text-cosmic-starWhite/60 hover:text-cosmic-goldLight'
                      }`}
                      data-testid="register-tab"
                    >
                      Register
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {!isLogin && (
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cosmic-gold/60" />
                      <input
                        type="text"
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="cosmic-input w-full pl-12"
                        required={!isLogin}
                        data-testid="name-input"
                      />
                    </div>
                  )}

                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cosmic-gold/60" />
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="cosmic-input w-full pl-12"
                      required
                      data-testid="email-input"
                    />
                  </div>

                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cosmic-gold/60" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="cosmic-input w-full pl-12 pr-12"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-cosmic-gold/60 hover:text-cosmic-goldLight"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {isLogin && (
                    <div className="text-right">
                      <Link
                        to="/forgot-password"
                        className="text-cosmic-goldLight/70 hover:text-cosmic-goldLight text-sm"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  )}

                  {error && (
                    <div className="text-red-400 text-sm text-center py-2 px-4 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="error-message">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="cosmic-btn w-full flex items-center justify-center space-x-2 disabled:opacity-50"
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-cosmic-goldLight/30 border-t-cosmic-goldLight rounded-full animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        <span>{isLogin ? 'Enter The Aura Space' : 'Begin Your Journey'}</span>
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-8 text-center">
                  <p className="text-cosmic-starWhite/50 text-sm font-cormorant">
                    {isLogin ? "Don't have an account? " : "Already have an account? "}
                    <button
                      onClick={() => setIsLogin(!isLogin)}
                      className="text-cosmic-goldLight hover:text-cosmic-gold transition-colors"
                    >
                      {isLogin ? 'Create one' : 'Sign in'}
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-center mt-8 text-cosmic-starWhite/30 text-xs font-cormorant">
          &copy; {new Date().getFullYear()} The Aura Space. All rights reserved.
        </p>
      </div>
    </div>
  );
}
